import React, { useMemo } from 'react'

import { isNil } from 'lodash-es'

import {
  ProviderInsightsCompetencyAreaLabels,
  ProviderInsightsCompetencyCategory,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { formatNumberToDecimalPlaces } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { CareInsightsMetrics } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'
import {
  CareInsightsSubsection,
  CareInsightsSubsectionProps,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/careInsightsSubsection/CareInsightsSubsection'

interface CulturallyResponsiveCareInsightsProps extends Partial<CareInsightsSubsectionProps> {
  metricInsights?: CareInsightsMetrics
}

export const CulturallyResponsiveCareInsights: React.FC<CulturallyResponsiveCareInsightsProps> = ({
  aiInsightsCurrentMonth,
  aiInsightsPastMonth,
  getClientInfoFromIds,
  isLoadingClients,
  isLoadingCareInsightsMetrics,
  isLoadingProviderInsights,
  isSupervisor,
  metricInsights,
  onClientPress,
  onSkillPress,
  onSupervisorUsageGuidelinesPress,
}) => {
  const culturallyResponsiveMetrics = useMemo(() => {
    if (!metricInsights) {
      return []
    }
    const {
      crc_alliance_score: crcAllianceScore,
      crc_program_average: crcAllianceProgramAverage,
      crc_max: crcAllianceMax,
    } = metricInsights
    return [
      {
        title: 'CRC Alliance',
        value:
          isNil(crcAllianceScore) || isNil(crcAllianceMax)
            ? undefined
            : formatNumberToDecimalPlaces({ value: crcAllianceScore, isPercentage: false }) + '/' + crcAllianceMax,
        programAverage: isNil(crcAllianceProgramAverage)
          ? undefined
          : formatNumberToDecimalPlaces({ value: crcAllianceProgramAverage, isPercentage: false }),
        tooltipContent:
          'This three-item scale measures your responsiveness to cultural factors in treatment, when a client endorses this is important to them in their care.',
        testID: 'CRCAllianceScore',
      },
    ]
  }, [metricInsights])

  return (
    <CareInsightsSubsection
      title={ProviderInsightsCompetencyAreaLabels[ProviderInsightsCompetencyCategory.CULTURALLY_RESPONSIVE]}
      competencyAreaName={ProviderInsightsCompetencyCategory.CULTURALLY_RESPONSIVE}
      testIDPrefix='CulturallyResponsiveCareInsights'
      aiInsightsCurrentMonth={aiInsightsCurrentMonth}
      aiInsightsPastMonth={aiInsightsPastMonth}
      getClientInfoFromIds={getClientInfoFromIds}
      isLoadingClients={isLoadingClients}
      isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
      isLoadingProviderInsights={isLoadingProviderInsights}
      isSupervisor={isSupervisor}
      metrics={culturallyResponsiveMetrics}
      metricsPerRow={1}
      onClientPress={onClientPress}
      onSkillPress={onSkillPress}
      onSupervisorUsageGuidelinesPress={onSupervisorUsageGuidelinesPress}
    />
  )
}
